import { toast } from "sonner";
import { DEMO_RUN, GOOGLE_CLIENT_ID, fetchGoogleClientId } from "@/config";
import {
  authService,
  setCurrentUser,
  clearCurrentUser,
} from "@/services/authService";
import { gmailService } from "@/services/gmailService";
import type { User } from "@/types/auth";
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";

// Helper to generate correlation IDs for structured logging
const generateCorrelationId = (): string =>
  `cid-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

// Logger wrapper for structured logging with context
const logger = {
  group: (name: string, fn: () => void) => {
    console.group(`[AuthContext] ${name}`);
    try {
      fn();
    } finally {
      console.groupEnd();
    }
  },
  info: (message: string, context?: Record<string, any>) => {
    console.log(
      `[AuthContext] ${message}`,
      context ? JSON.stringify(context, null, 2) : "",
    );
  },
  warn: (message: string, context?: Record<string, any>) => {
    console.warn(
      `[AuthContext] ${message}`,
      context ? JSON.stringify(context, null, 2) : "",
    );
  },
  error: (message: string, error?: Error | unknown) => {
    console.error(`[AuthContext] ${message}`, error);
  },
};

// Log the demo mode status
console.log("AuthContext - Demo mode enabled from config:", DEMO_RUN);

interface AuthContextType {
  user: User | null;
  users: User[];
  isLoading: boolean;
  signInWithCredentials: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  createUser: (user: {
    name: string;
    email: string;
    role: "user" | "admin";
  }) => Promise<void>;
  updateUserRole: (userId: string, role: "user" | "admin") => Promise<void>;
  requestGmailAccess: () => Promise<void>;
  getUsers: () => Promise<User[]>;
  login: (user: User | null) => void;
  logout: () => void;
  hasGmailAccess: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasGmailAccess, setHasGmailAccess] = useState(false); // Managed on backend, but context needs to know for UI

  const navigate = useNavigate();

  console.log("AuthProvider - Initial DEMO_RUN:", DEMO_RUN);

  const fetchCurrentUser = useCallback(async () => {
    const correlationId = generateCorrelationId();
    try {
      logger.info("Fetching current user from backend", {
        correlationId,
        timestamp: new Date().toISOString(),
      });
      setIsLoading(true);
      const response = await fetch("/api/auth/me", {
        credentials: "include",
        headers: {
          "X-Requested-With": "XMLHttpRequest", // CSRF protection
        },
      });

      if (response.ok) {
        const userData: User = await response.json();
        setUser(userData);
        setCurrentUser(userData);
        console.log("AuthContext - User authenticated:", userData.email);

        // Check for Gmail access status
        const gmailStatusResponse = await fetch("/api/auth/gmail-status", {
          credentials: "include",
          headers: {
            "X-Requested-With": "XMLHttpRequest",
          },
        });
        if (gmailStatusResponse.ok) {
          const { hasAccess } = await gmailStatusResponse.json();
          setHasGmailAccess(hasAccess);
        } else {
          setHasGmailAccess(false);
        }
      } else if (response.status === 401) {
        // Not authenticated - this is expected for users who haven't logged in yet
        const errorData = await response.json().catch(() => ({}));
        console.log(
          "AuthContext - User not authenticated:",
          errorData.code || "AUTH_REQUIRED",
        );
        setUser(null);
        setHasGmailAccess(false);
        // Don't show error toast for 401 - it's expected for unauthenticated users
      } else {
        console.error("Failed to fetch user:", response.statusText);
        setUser(null);
        setHasGmailAccess(false);
        toast.error("Failed to fetch user data.", {
          description: "Please try logging in again.",
        });
      }
    } catch (error) {
      console.error("Error fetching user:", error);
      setUser(null);
      setHasGmailAccess(false);
      // Only show network error if it's not a CORS/auth issue
      if (
        error instanceof TypeError &&
        error.message.includes("Failed to fetch")
      ) {
        toast.error("Network error during authentication.", {
          description: "Please check your connection.",
        });
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch current user on initial load and potentially after redirects
  useEffect(() => {
    fetchCurrentUser();
  }, [fetchCurrentUser]);

  // Handle redirects from backend (e.g., after login/logout)
  useEffect(() => {
    // We no longer read JWT from URL, but backend might redirect with status params
    const urlParams = new URLSearchParams(window.location.search);
    const authError = urlParams.get("error");
    const message = urlParams.get("message");

    if (authError) {
      toast.error("Authentication failed", {
        description: message || "An unknown error occurred during sign in.",
      });
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
      navigate("/login");
    }
  }, [navigate]);

  const signInWithCredentials = async (email: string, password: string) => {
    setIsLoading(true);
    try {
      // This path is likely for local user/pass auth, not Google.
      // If it exists, it should return a cookie-based session.
      // For now, assuming direct interaction with backend.
      const response = await fetch("/api/auth/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password }),
        credentials: "include", // Important to send/receive cookies
      });

      if (response.ok) {
        await fetchCurrentUser(); // Fetch user data from /api/auth/me after successful login
        toast.success("Signed in successfully", {
          description: `Welcome back!`,
        });
        navigate("/dashboard");
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || "Authentication failed");
      }
    } catch (error) {
      console.error("Sign in error:", error);
      toast.error("Sign in failed", {
        description:
          error instanceof Error ? error.message : "Please try again later",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const signInWithGoogle = async () => {
    const correlationId = generateCorrelationId();
    logger.info("Initiating Google OAuth flow", {
      correlationId,
      timestamp: new Date().toISOString(),
    });
    setIsLoading(true);
    try {
      console.log(
        "AuthProvider - Redirecting to server-side Google OAuth flow",
      );
      window.location.href = "/api/auth/google"; // Backend will handle the redirect and cookie
    } catch (error) {
      console.error("Sign in with Google error:", error);
      toast.error("Sign in failed", {
        description:
          error instanceof Error
            ? error.message
            : "An error occurred during Google sign in.",
      });
      setIsLoading(false);
    }
  };

  const requestGmailAccess = async () => {
    try {
      console.log(
        "AuthProvider - Redirecting to server-side Google OAuth flow for Gmail access",
      );
      // This will trigger re-authorization, potentially getting a fresh refresh token
      window.location.href = "/api/auth/google";
    } catch (error) {
      console.error("AuthProvider - Error requesting Gmail access:", error);
      toast.error("Gmail Access Failed", {
        description:
          error instanceof Error
            ? error.message
            : "Failed to enable Gmail access",
      });
    }
  };

  const signOut = async () => {
    const correlationId = generateCorrelationId();
    logger.info("Initiating logout", {
      correlationId,
      userId: user?.email || "unknown",
      timestamp: new Date().toISOString(),
    });
    setIsLoading(true);
    try {
      const response = await fetch("/api/auth/logout", {
        method: "POST", // Use POST for logout to prevent CSRF via GET
        credentials: "include",
      });
      if (response.ok) {
        setUser(null);
        setHasGmailAccess(false);
        clearCurrentUser();
        toast.success("Logged out successfully");
        navigate("/");
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || "Logout failed");
      }
    } catch (error) {
      console.error("Sign out error:", error);
      toast.error("Sign out failed", {
        description:
          error instanceof Error ? error.message : "Please try again later",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const createUser = async (newUser: {
    name: string;
    email: string;
    role: "user" | "admin";
  }): Promise<void> => {
    try {
      setIsLoading(true);
      await authService.createUser(newUser); // This should be an admin-only action
      await fetchCurrentUser(); // Re-fetch users to update state

      toast.success("User created", {
        description: `User ${newUser.name} created successfully.`,
      });
    } catch (error) {
      console.error("Error creating user:", error);
      toast.error("Failed to create user", {
        description:
          error instanceof Error ? error.message : "Please try again.",
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const updateUserRole = async (userId: string, role: "user" | "admin") => {
    try {
      setIsLoading(true);
      await authService.updateUserRole(userId, role);
      await fetchCurrentUser(); // Re-fetch users to update state

      toast.success("User role updated", {
        description: `User role updated to ${role}.`,
      });
    } catch (error) {
      console.error("Error updating user role:", error);
      toast.error("Failed to update user role", {
        description:
          error instanceof Error ? error.message : "Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getUsers = async (): Promise<User[]> => {
    try {
      const response = await fetch("/api/users", { credentials: "include" });
      if (response.ok) {
        const usersList: User[] = await response.json();
        setUsers(usersList);
        return usersList;
      } else {
        throw new Error("Failed to fetch users");
      }
    } catch (error) {
      console.error("Error loading users:", error);
      toast.error("Failed to load users", {
        description:
          error instanceof Error ? error.message : "Please try again.",
      });
      return [];
    }
  };

  const login = (userData: User | null) => {
    // This function is for direct user state manipulation, not actual authentication flow
    setUser(userData);
  };

  const logout = () => {
    // This function is for direct user state manipulation, not actual logout flow
    setUser(null);
    setHasGmailAccess(false);
  };

  const value = {
    user,
    users,
    isLoading,
    signInWithGoogle,
    signInWithCredentials,
    signOut,
    createUser,
    updateUserRole,
    requestGmailAccess,
    getUsers,
    login,
    logout,
    hasGmailAccess,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

export { useAuth };
